// The natural present-subjunctive bank: short, high-frequency things Brazilians
// actually say, with the verb in the blank. Utterance-first — each sentence
// starts from something people say out loud, then the verb fills the blank.
//
// The rule for every sentence here: if a Brazilian wouldn't say it out loud, it
// doesn't go in. That means:
//   • same subject → infinitive, never "que eu" (so no "Espero que eu fale");
//     "que eu" stays only with conseguir / poder and after tomara que, talvez,
//     pode ser que, é melhor que, que pena que — and in offers ("Quer que eu
//     ajude?"), where the subjects differ;
//   • "a gente", not "nós"; "mesmo que", not "embora"; "a não ser que", not "a
//     menos que"; "pode ser que", not "é possível que"; no "para que" (people say
//     "pra" + infinitive, which isn't subjunctive);
//   • everyday verbs: deixar, entender, pedir desculpas.

import verbData from '../../data/verbs.json'
import type { DrillPronoun } from './drillPool'

export type Person = 'eu' | 'tu' | 'você' | 'ele/ela' | 'a gente' | 'vocês' | 'eles/elas'

export type Family =
  | 'imperative'
  | 'tomara'
  | 'quer'
  | 'quero'
  | 'podeser'
  | 'talvez'
  | 'melhor'
  | 'pena'
  | 'espero'
  | 'duvido'
  | 'naoacho'
  | 'caso'
  | 'mesmoque'
  | 'desdeque'
  | 'ateque'
  | 'antesque'
  | 'anaoserque'
  | 'nemque'
  | 'pormaisque'

export type NaturalItem = {
  // The sentence, with ___ where the verb goes.
  prompt: string
  // The infinitive shown in parentheses — "preocupar-se" for a reflexive.
  verb: string
  // The present-subjunctive form, including any reflexive: "se preocupe".
  answer: string
  // The English gloss shown after answering.
  en: string
  // Who the sentence naturally takes. Several persons can share one form
  // (você and ele/ela are both "venha"); they're not forced to cover every person.
  persons: Person[]
  family: Family
}

function n(family: Family, prompt: string, verb: string, answer: string, en: string, persons: Person[]): NaturalItem {
  return { prompt, verb, answer, en, persons, family }
}

const VOCE: Person[] = ['você']
const ELE: Person[] = ['ele/ela', 'você']
const EU: Person[] = ['eu']
const GENTE: Person[] = ['a gente']
const VOCES: Person[] = ['vocês']

export const NATURAL_ITEMS: NaturalItem[] = [
  // ── A. Imperatives — você forms, which are the present subjunctive ────────
  n('imperative', 'Não ___ com isso.', 'preocupar-se', 'se preocupe', "Don't worry about it.", VOCE),
  n('imperative', 'Não ___ o guarda-chuva.', 'esquecer', 'esqueça', "Don't forget the umbrella.", VOCE),
  n('imperative', 'Não ___ de me ligar.', 'esquecer-se', 'se esqueça', "Don't forget to call me.", VOCE),
  n('imperative', 'Não ___ barulho.', 'fazer', 'faça', "Don't make noise.", VOCE),
  n('imperative', 'Não ___ alto.', 'falar', 'fale', "Don't talk loudly.", VOCE),
  n('imperative', '___ tranquilo.', 'ficar', 'fique', 'Relax.', VOCE),
  n('imperative', '___ calma.', 'ter', 'tenha', 'Calm down.', VOCE),
  n('imperative', '___ paciência.', 'ter', 'tenha', 'Be patient.', VOCE),
  n('imperative', '___ cá.', 'vir', 'venha', 'Come here.', VOCE),
  n('imperative', 'Me ___ caso precise.', 'avisar', 'avise', 'Let me know if you need anything.', VOCE),
  n('imperative', '___ a verdade.', 'dizer', 'diga', 'Tell the truth.', VOCE),
  n('imperative', '___ um documento com foto.', 'trazer', 'traga', 'Bring a photo ID.', VOCE),
  n('imperative', '___ pra lá.', 'deixar', 'deixe', 'Forget it.', VOCE),
  n('imperative', '___ bem antes de decidir.', 'pensar', 'pense', 'Think carefully before deciding.', VOCE),
  n('imperative', '___ os dois lados.', 'olhar', 'olhe', 'Look both ways.', VOCE),
  n('imperative', '___ se ele está aí.', 'ver', 'veja', "See if he's there.", VOCE),
  n('imperative', '___ paciente.', 'ser', 'seja', 'Be patient.', VOCE),
  n('imperative', '___ uma olhada.', 'dar', 'dê', 'Take a look.', VOCE),
  n('imperative', '___ com calma.', 'ir', 'vá', 'Take it easy.', VOCE),
  n('imperative', '___ a porta.', 'fechar', 'feche', 'Close the door.', VOCE),
  n('imperative', '___ a janela.', 'abrir', 'abra', 'Open the window.', VOCE),
  n('imperative', '___ o celular.', 'desligar', 'desligue', 'Turn off your phone.', VOCE),
  n('imperative', '___ à vontade.', 'ficar', 'fique', 'Make yourself at home.', VOCE),
  n('imperative', '___ bem.', 'dormir', 'durma', 'Sleep well.', VOCE),
  n('imperative', '___ desculpas pra ela.', 'pedir', 'peça', 'Apologize to her.', VOCE),
  n('imperative', '___ em frente até o semáforo.', 'seguir', 'siga', 'Go straight ahead until the traffic light.', VOCE),
  n('imperative', '___, por favor.', 'repetir', 'repita', 'Please repeat that.', VOCE),
  n('imperative', 'Não ___.', 'insistir', 'insista', "Don't insist.", VOCE),
  n('imperative', '___ esse vídeo.', 'assistir', 'assista', 'Watch this video.', VOCE),
  n('imperative', 'Não ___ com ele.', 'discutir', 'discuta', "Don't argue with him.", VOCE),
  n('imperative', '___ aqui.', 'subir', 'suba', 'Come up here.', VOCE),
  n('imperative', '___ bastante água.', 'beber', 'beba', 'Drink plenty of water.', VOCE),
  n('imperative', '___ alguma coisa antes de sair.', 'comer', 'coma', 'Eat something before you go.', VOCE),
  // …and the plural, for vocês
  n('imperative', '___ tranquilos, pessoal.', 'ficar', 'fiquem', 'Relax, everyone.', VOCES),
  n('imperative', '___ cá, pessoal.', 'vir', 'venham', 'Come here, everyone.', VOCES),
  n('imperative', 'Não ___ com isso, gente.', 'preocupar-se', 'se preocupem', "Don't worry about it, you all.", VOCES),

  // ── B. Tomara que ─────────────────────────────────────────────────────────
  n('tomara', 'Tomara que ___ certo.', 'dar', 'dê', 'I hope it works out.', ELE),
  n('tomara', 'Tomara que não ___.', 'chover', 'chova', "I hope it doesn't rain.", ELE),
  n('tomara', 'Tomara que ele ___.', 'vir', 'venha', 'I hope he comes.', ELE),
  n('tomara', 'Tomara que ela ___.', 'conseguir', 'consiga', 'I hope she makes it.', ELE),
  n('tomara', 'Tomara que você ___.', 'gostar', 'goste', 'I hope you like it.', VOCE),
  n('tomara', 'Tomara que vocês ___.', 'conseguir', 'consigam', 'I hope you all make it.', VOCES),
  n('tomara', 'Tomara que eu ___.', 'passar', 'passe', 'I hope I pass.', EU),
  n('tomara', 'Tomara que a gente ___.', 'conseguir', 'consiga', 'I hope we make it.', GENTE),

  // ── C. Offers — "que eu" is right here: you ask, I help ──────────────────
  n('quer', 'Quer que eu ___?', 'ajudar', 'ajude', 'Do you want me to help?', EU),
  n('quer', 'Quer que eu ___?', 'esperar', 'espere', 'Do you want me to wait?', EU),
  n('quer', 'Quer que eu ___?', 'levar', 'leve', 'Do you want me to take it?', EU),
  n('quer', 'Quer que eu ___?', 'fazer', 'faça', 'Do you want me to do it?', EU),
  n('quer', 'Quer que a gente ___?', 'esperar', 'espere', 'Do you want us to wait?', GENTE),

  // ── D. Querer / precisar — different subjects ────────────────────────────
  n('quero', 'Quero que você me ___.', 'avisar', 'avise', 'I want you to let me know.', VOCE),
  n('quero', 'Preciso que você ___ aqui.', 'assinar', 'assine', 'I need you to sign here.', VOCE),
  n('quero', 'Preciso que a gente ___ isso hoje.', 'fazer', 'faça', 'I need us to do this today.', GENTE),
  n('quero', 'Não quero que você ___.', 'preocupar-se', 'se preocupe', "I don't want you to worry.", VOCE),
  n('quero', 'Quero que ele me ___.', 'ligar', 'ligue', 'I want him to call me.', ELE),
  n('quero', 'Quero que vocês me ___.', 'avisar', 'avisem', 'I want you all to let me know.', VOCES),
  n('quero', 'Preciso que você me ___ uma mão.', 'dar', 'dê', 'I need you to give me a hand.', VOCE),

  // ── E. Pode ser que / Talvez / É melhor que / Que pena que ───────────────
  n('podeser', 'Pode ser que ___.', 'chover', 'chova', 'It might rain.', ELE),
  n('podeser', 'Pode ser que ele não ___.', 'vir', 'venha', "He might not come.", ELE),
  n('podeser', 'Pode ser que ___ certo.', 'dar', 'dê', 'It might work out.', ELE),
  n('podeser', 'Pode ser que eu ___ aí amanhã.', 'passar', 'passe', 'I might stop by tomorrow.', EU),
  n('talvez', 'Talvez ele ___ amanhã.', 'vir', 'venha', "Maybe he'll come tomorrow.", ELE),
  n('talvez', 'Talvez ___ mais tarde.', 'chover', 'chova', "Maybe it'll rain later.", ELE),
  n('talvez', 'Talvez eu ___ mais tarde.', 'voltar', 'volte', "Maybe I'll come back later.", EU),
  n('talvez', 'Talvez eu ___ um pouco.', 'demorar', 'demore', 'I might be a while.', EU),
  n('talvez', 'Talvez eu ___ aí.', 'passar', 'passe', "Maybe I'll stop by.", EU),
  n('melhor', 'É melhor que você ___ em casa.', 'ficar', 'fique', "It's better if you stay home.", VOCE),
  n('melhor', 'É melhor que a gente ___ cedo.', 'ir', 'vá', "It's better if we go early.", GENTE),
  n('pena', 'Que pena que você não ___ vir.', 'poder', 'possa', "What a shame that you can't come.", VOCE),

  // ── F. Espero que — a different subject only ─────────────────────────────
  n('espero', 'Espero que você ___.', 'entender', 'entenda', 'I hope you understand.', VOCE),
  n('espero', 'Espero que ___ tudo certo.', 'dar', 'dê', 'I hope everything works out.', ELE),
  n('espero', 'Espero que ele ___.', 'gostar', 'goste', 'I hope he likes it.', ELE),
  n('espero', 'Espero que você ___.', 'conseguir', 'consiga', 'I hope you make it.', VOCE),
  n('espero', 'Espero que vocês ___.', 'gostar', 'gostem', 'I hope you all like it.', VOCES),
  n('espero', 'Espero que ele ___ logo.', 'decidir', 'decida', 'I hope he decides soon.', ELE),
  n('espero', 'Espero que você ___ melhor.', 'sentir-se', 'se sinta', 'I hope you feel better.', VOCE),
  n('espero', 'Espero que você ___ bem.', 'dormir', 'durma', 'I hope you sleep well.', VOCE),
  n('espero', 'Espero que eu ___ ir.', 'poder', 'possa', 'I hope I can go.', EU),

  // ── G. Duvido / não acho que — a different subject only ──────────────────
  n('duvido', 'Duvido que ele ___.', 'conseguir', 'consiga', "I doubt he'll manage.", ELE),
  n('duvido', 'Duvido que ___ certo.', 'dar', 'dê', "I doubt it'll work out.", ELE),
  n('duvido', 'Duvido que eu ___.', 'conseguir', 'consiga', "I doubt I'll manage.", EU),
  n('duvido', 'Duvido que ele ___ a verdade.', 'dizer', 'diga', "I doubt he'll tell the truth.", ELE),
  n('duvido', 'Duvido que ela ___ vir.', 'poder', 'possa', "I doubt she can come.", ELE),
  n('naoacho', 'Não acho que ele ___.', 'saber', 'saiba', "I don't think he knows.", ELE),

  // ── H. Caso ───────────────────────────────────────────────────────────────
  n('caso', 'Me avise caso ___.', 'precisar', 'precise', 'Let me know if you need anything.', VOCE),
  n('caso', 'Caso ___, é só me chamar.', 'querer', 'queira', 'If you want, just call me.', VOCE),

  // ── Other conjunctions that take the subjunctive, in the spoken register ──
  n('mesmoque', 'Vou mesmo que ___.', 'chover', 'chova', "I'm going even if it rains.", ELE),
  n('mesmoque', 'Eu vou, mesmo que você não ___.', 'querer', 'queira', "I'm going even if you don't want to.", VOCE),
  n('desdeque', 'Pode ir, desde que ___ cedo.', 'voltar', 'volte', "You can go, as long as you're back early.", VOCE),
  n('desdeque', 'Eu empresto, desde que você ___ cuidado.', 'ter', 'tenha', "I'll lend it, as long as you're careful.", VOCE),
  n('ateque', 'Vou esperar até que ele ___.', 'chegar', 'chegue', "I'll wait until he arrives.", ELE),
  n('antesque', 'Vou sair antes que ___.', 'chover', 'chova', "I'm leaving before it rains.", ELE),
  n('antesque', 'Vamos embora antes que ___ tarde.', 'ficar', 'fique', "Let's go before it gets late.", ELE),
  n('anaoserque', 'Vamos sair, a não ser que ___.', 'chover', 'chova', "We're going out, unless it rains.", ELE),
  n('anaoserque', 'A gente vai, a não ser que você ___ ficar.', 'preferir', 'prefira', "We're going, unless you'd rather stay.", VOCE),
  // "nem que" (a stubborn "even if") and "por mais que" (however much) — both spoken, both subjunctive.
  n('nemque', 'Eu vou nem que ___.', 'chover', 'chova', "I'm going even if it rains.", ELE),
  n('nemque', 'Eu faço, nem que ___ difícil.', 'ser', 'seja', "I'll do it even if it's hard.", ELE),
  n('nemque', 'Eu espero, nem que ___ até amanhã.', 'demorar', 'demore', "I'll wait even if it takes until tomorrow.", ELE),
  n('pormaisque', 'Por mais que eu ___, não consigo.', 'tentar', 'tente', "However hard I try, I can't.", EU),
  n('pormaisque', 'Por mais que eu ___, esqueço tudo.', 'estudar', 'estude', 'However much I study, I forget everything.', EU),
  n('pormaisque', 'Por mais que ele ___, ninguém escuta.', 'falar', 'fale', 'However much he talks, nobody listens.', ELE),
  n('pormaisque', 'Por mais que você ___, ela não muda.', 'pedir', 'peça', 'However much you ask, she never changes.', VOCE),
]

// ── Helpers ─────────────────────────────────────────────────────────────────

// Which drill pronoun checkbox each person falls under (você / ela / ele share one;
// vocês / elas / eles another; "a gente" is practised under nós).
export const PERSON_PRONOUN: Record<Person, DrillPronoun> = {
  eu: 'eu',
  tu: 'tu',
  você: 'você',
  'ele/ela': 'você',
  'a gente': 'a gente',
  vocês: 'eles',
  'eles/elas': 'eles',
}

// "preocupar-se" → "preocupar": the verb the conjugator and verbs.json know.
export function baseVerb(verb: string): string {
  return verb.replace(/-se$/, '')
}

const CORE_TYPES = new Map<string, string>((verbData.verbs as Array<{ infinitive: string; verbType: string }>).map((verb) => [verb.infinitive, verb.verbType]))

// The drill's verb-type filter for a verb: from verbs.json when it has it, else by
// its ending (every other verb used here is regular).
export function naturalVerbType(verb: string): string {
  const base = baseVerb(verb)
  const core = CORE_TYPES.get(base)
  if (core) return core
  if (base.endsWith('ar')) return 'regular-ar'
  if (base.endsWith('er')) return 'regular-er'
  return 'regular-ir'
}

// The items that match the drill's current verb-type and pronoun checkboxes. An
// item is offered when at least one of its persons is a ticked pronoun.
export function naturalPool(settings: { verbTypes: readonly string[]; pronouns: readonly string[] }): NaturalItem[] {
  return NATURAL_ITEMS.filter(
    (item) =>
      settings.verbTypes.includes(naturalVerbType(item.verb)) && item.persons.some((person) => settings.pronouns.includes(PERSON_PRONOUN[person])),
  )
}

// "Quer que eu ___?" → { before: "Quer que eu ", after: "?" }
export function splitPrompt(prompt: string): { before: string; after: string } {
  const [before, after, ...rest] = prompt.split('___')
  if (after === undefined || rest.length > 0) throw new Error(`A natural prompt needs exactly one blank: ${prompt}`)
  return { before, after }
}
